import { Stack } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { MinusIcon } from '@/components/icons/MinusIcon';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { COLORS } from '@/styles/tokens/colors';

// Zoom constants
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3.0;
const ZOOM_STEP = 0.25;

interface PDFZoomControlsProps {
  onZoomLevelChange: (zoomLevel: number) => void;
  /** When provided, the internal zoom state is synced to this value (e.g. after an auto-zoom). */
  externalZoomLevel?: number;
}

export function PDFZoomControls({
  onZoomLevelChange,
  externalZoomLevel,
}: PDFZoomControlsProps) {
  // Zoom state management
  const [zoomLevel, setZoomLevel] = useState(1.0);

  // Track whether the zoom change originated from the external prop so we can
  // avoid firing onZoomLevelChange for externally-driven changes (the parent
  // already knows the value it set).
  const isSyncingExternalRef = useRef(false);

  // Mirror of zoomLevel as a ref so the sync effect can read the latest
  // internal value without adding it to the dependency array (which would
  // cause infinite loops).
  const zoomLevelRef = useRef(zoomLevel);
  zoomLevelRef.current = zoomLevel;

  // Sync internal state when an external zoom level is provided (e.g. auto-zoom).
  // Only set the sync flag when the value actually differs from the current
  // internal zoom — otherwise setZoomLevel is a no-op, the callback effect
  // never fires, and isSyncingExternalRef stays true, swallowing the next
  // user-initiated +/- click.
  useEffect(() => {
    if (
      externalZoomLevel != null &&
      externalZoomLevel !== zoomLevelRef.current
    ) {
      isSyncingExternalRef.current = true;
      setZoomLevel(externalZoomLevel);
    }
  }, [externalZoomLevel]);

  const handleZoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel((prev) => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
  }, []);

  // Skip the very first render so the initial zoom (1.0) isn't reported as a
  // user-initiated change — that would disable auto-zoom before it can fire.
  const isInitialMountRef = useRef(true);

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    // Skip firing the callback when the change came from an external sync
    // to avoid telling the parent about a value it already set.
    if (isSyncingExternalRef.current) {
      isSyncingExternalRef.current = false;
      return;
    }
    onZoomLevelChange(zoomLevel);
  }, [zoomLevel, onZoomLevelChange]);

  const canZoomOut = zoomLevel > MIN_ZOOM;
  const canZoomIn = zoomLevel < MAX_ZOOM;

  return (
    <Stack direction="row" spacing={0.5} bgcolor={COLORS.PRIMITIVES.WHITE}>
      <IconButton
        icon={MinusIcon}
        ariaLabel="Zoom out"
        size="sm"
        variant="secondary"
        onClick={handleZoomOut}
        disabled={!canZoomOut}
        data-testid="PDFZoomControls-zoomOut"
      />
      <IconButton
        icon={PlusIcon}
        ariaLabel="Zoom in"
        size="sm"
        variant="secondary"
        onClick={handleZoomIn}
        disabled={!canZoomIn}
        data-testid="PDFZoomControls-zoomIn"
      />
    </Stack>
  );
}
